import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import JsonLd from "@/components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "";

const linksPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "실전 일본어 연구소 | 링크 모음",
  "description": "실전 일본어 연구소의 홈페이지, 인스타그램, 카카오채널 링크 모음입니다.",
  "url": siteUrl ? `${siteUrl}/links` : "",
  "isPartOf": {
    "@type": "WebSite",
    "name": "실전 일본어 연구소",
    "url": siteUrl
  }
};

interface LinkItem {
  id: string;
  label: string;
  description: string;
  url: string;
  icon: string;
  color: string;
  external: boolean;
}

const LINKS: LinkItem[] = [
  {
    id: "homepage",
    label: "실전 일본어 연구소",
    description: "8주 완성 소수정예 온라인 일본어 클래스",
    url: "/",
    icon: "ri-global-line",
    color: "bg-primary-500",
    external: false,
  },
  {
    id: "instagram",
    label: "인스타그램",
    description: "수업 소식과 일본어 꿀팁을 가장 빠르게",
    url: "https://www.instagram.com/siljeon.jp_lab?igsh=MWdsN3R6NmlmcDg3eg%3D%3D&utm_source=qr",
    icon: "ri-instagram-line",
    color: "bg-accent-500",
    external: true,
  },
  {
    id: "kakao",
    label: "카카오채널",
    description: "간편하게 1:1 문의하고 수업 상담받기",
    url: "http://pf.kakao.com/_VxdxgxnX",
    icon: "ri-kakao-talk-fill",
    color: "bg-secondary-500",
    external: true,
  },
];

export default function LinksPage() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    document.title = "실전 일본어 연구소 | 링크 모음";
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <main className="min-h-screen bg-background-50 relative overflow-hidden flex flex-col items-center justify-center px-4 py-12 md:py-16">
      <JsonLd data={linksPageSchema} />
      {/* Decorative blobs */}
      <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-primary-100/50 blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-accent-100/40 blur-3xl pointer-events-none"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-secondary-100/30 blur-3xl pointer-events-none"></div>

      <div className="relative w-full max-w-md z-10">
        {/* Logo & Brand */}
        <div
          className={`flex flex-col items-center text-center transition-all duration-700 ${
            loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading text-3xl md:text-4xl font-black">
            日
          </span>
          <h1 className="mt-5 font-heading text-2xl md:text-3xl font-black text-foreground-950 tracking-tight">
            실전 일본어 연구소
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600 leading-relaxed max-w-xs">
            이해하고, 직접 말하는 일본어
          </p>
        </div>

        {/* Link Cards */}
        <div className="mt-10 space-y-3.5">
          {LINKS.map((link, index) => (
            <LinkCard
              key={link.id}
              link={link}
              index={index}
              loaded={loaded}
            />
          ))}
        </div>

        {/* Footer */}
        <div
          className={`mt-12 text-center transition-all duration-700 delay-500 ${
            loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <p className="text-xs text-foreground-500">
            © 2026 실전 일본어 연구소. All rights reserved.
          </p>
          <div className="mt-3 flex items-center justify-center gap-4">
            <Link
              to="/privacy"
              className="text-xs text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              개인정보 처리방침
            </Link>
            <Link
              to="/terms"
              className="text-xs text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              이용약관
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

function LinkCard({
  link,
  index,
  loaded,
}: {
  link: LinkItem;
  index: number;
  loaded: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const delay = 150 + index * 100;

  const content = (
    <div
      className={`group relative flex items-center gap-4 px-5 py-4 md:px-6 md:py-5 rounded-xl border border-background-200 bg-background-50 transition-all duration-300 cursor-pointer ${
        hovered ? "border-primary-300 bg-primary-50/60 translate-x-1" : ""
      }`}
    >
      {/* Icon */}
      <span
        className={`w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-xl ${link.color} text-background-50 text-lg md:text-xl shrink-0 transition-transform duration-300 ${
          hovered ? "scale-110" : ""
        }`}
      >
        <i className={link.icon}></i>
      </span>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className="font-heading text-sm md:text-base font-bold text-foreground-950 whitespace-nowrap">
          {link.label}
        </p>
        <p className="text-xs md:text-sm text-foreground-600 mt-0.5 leading-snug truncate">
          {link.description}
        </p>
      </div>

      {/* Arrow */}
      <span
        className={`w-6 h-6 flex items-center justify-center shrink-0 transition-all duration-300 ${
          hovered ? "text-primary-500 translate-x-1" : "text-foreground-400"
        }`}
      >
        <i className={`${link.external ? "ri-external-link-line" : "ri-arrow-right-line"} text-sm`}></i>
      </span>
    </div>
  );

  const transitionStyle = {
    transition: `all 0.6s cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}ms`,
    ...(loaded
      ? { opacity: 1, transform: "translateY(0)" }
      : { opacity: 0, transform: "translateY(16px)" }),
  };

  if (link.external) {
    return (
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        style={transitionStyle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      to={link.url}
      style={transitionStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {content}
    </Link>
  );
}